import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOUs } from '@/hooks/useOUs';
import { FREQUENCY_KEYS, frequencyLabelKey } from '../tabs/SurveysTab/surveyFrequency';

export interface ActiveFilterState {
  col: string;
  top: number;
  left: number;
}

interface ColFilterPopoverProps {
  col: string;
  top: number;
  left: number;
  filters: Record<string, string>;
  onFilter: (col: string, val: string) => void;
  onClose: () => void;
}

export function ColFilterPopover({ col, top, left, filters, onFilter, onClose }: ColFilterPopoverProps) {
  const { t } = useTranslation();
  const { data: ous = [] } = useOUs();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', h); };
  }, [onClose]);

  const style: React.CSSProperties = { position: 'fixed', top, left };

  const radioGroup = (key: string, opts: Array<[string, string]>) => (
    <div style={style} className="col-flt-pop" ref={ref}>
      {opts.map(([v, label]) => (
        <label key={v} className="col-flt-opt">
          <input
            type="radio"
            name={`flt-${key}`}
            checked={(filters[key] ?? '') === v}
            onChange={() => onFilter(key, v)}
          />
          {label}
        </label>
      ))}
    </div>
  );

  if (col === 'name') return (
    <div style={style} className="col-flt-pop" ref={ref}>
      <input
        type="text"
        className="col-flt-input"
        placeholder={t('admin.filterSearchName')}
        value={filters.name ?? ''}
        onChange={e => onFilter('name', e.target.value)}
        autoFocus
      />
    </div>
  );

  if (col === 'createdBy') return (
    <div style={style} className="col-flt-pop" ref={ref}>
      <input
        type="text"
        className="col-flt-input"
        placeholder={t('admin.filterSearchCreator')}
        value={filters.createdBy ?? ''}
        onChange={e => onFilter('createdBy', e.target.value)}
        autoFocus
      />
    </div>
  );

  if (col === 'status') return radioGroup('status', [
    ['', t('admin.filterAll')],
    ['active', t('admin.statusActive')],
    ['paused', t('admin.statusPaused')],
    ['inactive', t('admin.statusInactive')],
    ['pending_approval', t('admin.statusPending')],
  ]);

  if (col === 'audience') return radioGroup('audience', [
    ['', t('admin.filterAll')],
    ['Global', 'Global'],
    ['Managers', 'Managers'],
    ...ous.map(ou => [ou.label, ou.label] as [string, string]),
  ]);

  // D-3: las opciones son las claves de `surveyFrequency.ts`, no cadenas sueltas en español —
  // así el filtro sigue funcionando en los 7 idiomas.
  if (col === 'frequency') return radioGroup('frequency', [
    ['', t('admin.filterAll')],
    ...FREQUENCY_KEYS.map(key => [key, t(frequencyLabelKey(key))] as [string, string]),
  ]);

  if (col === 'period') return (
    <div style={style} className="col-flt-pop" ref={ref}>
      <div className="col-flt-date-row">
        <label>{t('admin.filterFrom')}</label>
        <input
          type="date"
          className="col-flt-input"
          value={filters.periodStart ?? ''}
          onChange={e => onFilter('periodStart', e.target.value)}
        />
      </div>
      <div className="col-flt-date-row">
        <label>{t('admin.filterTo')}</label>
        <input
          type="date"
          className="col-flt-input"
          value={filters.periodEnd ?? ''}
          onChange={e => onFilter('periodEnd', e.target.value)}
        />
      </div>
      <button
        className="col-flt-clear"
        onClick={() => { onFilter('periodStart', ''); onFilter('periodEnd', ''); onClose(); }}
      >
        {t('admin.filterClearDates')}
      </button>
    </div>
  );

  return null;
}

interface FltBtnProps {
  col: string;
  active: boolean;
  hasFilter: boolean;
  onOpen: (col: string, e: React.MouseEvent<HTMLButtonElement>) => void;
}

export function FltBtn({ col, active, hasFilter, onOpen }: FltBtnProps) {
  return (
    <button
      className={`col-flt-btn${active ? ' active' : ''}${hasFilter ? ' has-filter' : ''}`}
      data-flt-col={col}
      onClick={e => { e.stopPropagation(); onOpen(col, e); }}
    >
      <i className="fas fa-chevron-down" />
    </button>
  );
}
